/**
 * Print a page made for paper (a badge, an e-ticket) without leaving the console: the page goes
 * into a hidden frame, and the frame prints. The console stays as it is.
 *
 * A browser asks before it prints. A kiosk station can print with no question: start Chrome
 * with `--kiosk-printing`.
 */

const ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

/** Text for an HTML page. Every name on a badge is typed by somebody else. */
export const escapeHtml = (text: string) =>
  text.replaceAll(/[&<>"']/g, (char) => ESCAPES[char] ?? char)

/** How long the frame stays after the print dialog closes or fails to open. */
const REMOVE_AFTER_MS = 60_000

export function printDocument(html: string): void {
  const frame = document.createElement('iframe')
  frame.setAttribute('aria-hidden', 'true')
  frame.tabIndex = -1
  frame.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden'
  let removed = false
  const remove = () => {
    if (removed) return
    removed = true
    frame.remove()
  }
  frame.addEventListener('load', () => {
    const view = frame.contentWindow
    if (view === null) return
    view.addEventListener('afterprint', () => setTimeout(remove, 0))
    view.focus()
    view.print()
  })
  frame.srcdoc = html
  document.body.append(frame)
  setTimeout(remove, REMOVE_AFTER_MS)
}
